import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Trash2, Camera, Save } from 'lucide-react';
import {
  processesService,
  ProcessFrequency,
  ProcessTask,
  CreateProcessDto,
  UpdateProcessDto,
} from '@/services/processes.service';
import { Button, Card, Input, Label, useToast } from '@caruso/ui';

interface TaskFormData {
  id?: string;
  name: string;
  description?: string;
  order: number;
  minTime: string;
  maxTime: string;
  photoRequired: boolean;
  active?: boolean;
  isNew?: boolean;
}

const frequencyOptions = [
  { value: ProcessFrequency.DAILY, label: 'Diário' },
  { value: ProcessFrequency.WEEKLY, label: 'Semanal' },
  { value: ProcessFrequency.MONTHLY, label: 'Mensal' },
];

export function ProcessFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [frequency, setFrequency] = useState<ProcessFrequency>(ProcessFrequency.DAILY);
  const [active, setActive] = useState(true);
  const [tasks, setTasks] = useState<TaskFormData[]>([
    {
      name: '',
      description: '',
      order: 0,
      minTime: '08:00',
      maxTime: '18:00',
      photoRequired: false,
      isNew: true,
    },
  ]);

  const { data: process, isLoading } = useQuery({
    queryKey: ['process', id],
    queryFn: () => processesService.getById(id!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (process) {
      setName(process.name);
      setDescription(process.description || '');
      setFrequency(process.frequency);
      setActive(process.active);
      setTasks(
        process.tasks.map((task) => ({
          ...task,
          isNew: false,
        }))
      );
    }
  }, [process]);

  const createMutation = useMutation({
    mutationFn: (data: CreateProcessDto) => processesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes'] });
      toast({
        title: 'Processo criado!',
        description: 'O processo foi criado com sucesso.',
        variant: 'success',
      });
      navigate('/app/processos');
    },
    onError: () => {
      toast({
        title: 'Erro!',
        description: 'Não foi possível criar o processo.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateProcessDto) => processesService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes'] });
      queryClient.invalidateQueries({ queryKey: ['process', id] });
      toast({
        title: 'Processo atualizado!',
        description: 'O processo foi atualizado com sucesso.',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro!',
        description: 'Não foi possível atualizar o processo.',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name) {
      toast({
        title: 'Erro!',
        description: 'Nome do processo é obrigatório.',
        variant: 'destructive',
      });
      return;
    }

    if (isEditing) {
      // Update process basic info
      await updateMutation.mutateAsync({
        name,
        description,
        frequency,
        active,
      });

      // Handle tasks
      for (const task of tasks) {
        if (task.isNew) {
          // Create new task
          await processesService.addTask(id!, {
            name: task.name,
            description: task.description,
            order: task.order,
            minTime: task.minTime,
            maxTime: task.maxTime,
            photoRequired: task.photoRequired,
          });
        } else if (task.id) {
          // Update existing task
          await processesService.updateTask(task.id, {
            name: task.name,
            description: task.description,
            order: task.order,
            minTime: task.minTime,
            maxTime: task.maxTime,
            photoRequired: task.photoRequired,
            active: task.active,
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['processes'] });
      queryClient.invalidateQueries({ queryKey: ['process', id] });
      navigate('/app/processos');
    } else {
      const createData: CreateProcessDto = {
        name,
        description,
        frequency,
        tasks: tasks.map((task, index) => ({
          name: task.name,
          description: task.description,
          order: index,
          minTime: task.minTime,
          maxTime: task.maxTime,
          photoRequired: task.photoRequired,
        })),
      };
      createMutation.mutate(createData);
    }
  };

  const addTask = () => {
    setTasks([
      ...tasks,
      {
        name: '',
        description: '',
        order: tasks.length,
        minTime: '08:00',
        maxTime: '18:00',
        photoRequired: false,
        isNew: true,
      },
    ]);
  };

  const removeTask = async (index: number) => {
    const task = tasks[index];

    if (task.id && !task.isNew) {
      // Delete from server if it's an existing task
      try {
        await processesService.deleteTask(task.id);
        toast({
          title: 'Tarefa removida',
          variant: 'success',
        });
        queryClient.invalidateQueries({ queryKey: ['process', id] });
      } catch (error) {
        toast({
          title: 'Erro ao remover tarefa',
          variant: 'destructive',
        });
        return;
      }
    }

    setTasks(tasks.filter((_, i) => i !== index));
  };

  const updateTask = (index: number, field: keyof TaskFormData, value: any) => {
    const newTasks = [...tasks];
    newTasks[index] = { ...newTasks[index], [field]: value };
    setTasks(newTasks);
  };

  if (isEditing && isLoading) {
    return (
      <div className="text-center py-12 text-brand-ivory/50">Carregando...</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/app/processos')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-serif text-brand-gold">
              {isEditing ? 'Editar Processo' : 'Novo Processo'}
            </h1>
            <p className="text-brand-ivory/60 mt-1">
              {isEditing ? 'Atualize as informações do processo' : 'Crie um novo processo'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Process Information */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-6">
              <h2 className="text-xl font-serif text-brand-gold mb-6">Informações do Processo</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="name" className="text-brand-ivory">
                    Nome do Processo *
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex: Limpeza da Loja"
                    className="bg-brand-smoke/50 border-brand-gold/20"
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description" className="text-brand-ivory">
                    Descrição
                  </Label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descreva o processo"
                    rows={3}
                    className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-md text-brand-ivory placeholder:text-brand-ivory/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-transparent resize-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="frequency" className="text-brand-ivory">
                    Frequência de Execução *
                  </Label>
                  <select
                    id="frequency"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value as ProcessFrequency)}
                    className="w-full h-10 px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-md text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-transparent"
                  >
                    {frequencyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-brand-ivory">Status</Label>
                  <div className="flex items-center space-x-2 pt-2">
                    <input
                      type="checkbox"
                      id="active"
                      checked={active}
                      onChange={(e) => setActive(e.target.checked)}
                      className="w-4 h-4 rounded border-brand-gold/20 bg-brand-smoke/50 text-brand-gold focus:ring-brand-gold focus:ring-offset-0"
                    />
                    <Label htmlFor="active" className="cursor-pointer text-brand-ivory">
                      {active ? 'Ativo' : 'Inativo'}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Tasks Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-serif text-brand-gold">Tarefas do Processo</h2>
                <Button type="button" onClick={addTask} variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar Tarefa
                </Button>
              </div>

              {tasks.length === 0 ? (
                <div className="text-center py-8 text-brand-ivory/50">
                  <p>Nenhuma tarefa adicionada ainda</p>
                  <Button
                    type="button"
                    onClick={addTask}
                    variant="outline"
                    className="mt-4"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Adicionar Primeira Tarefa
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {tasks.map((task, index) => (
                    <motion.div
                      key={task.id || `new-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-brand-smoke/30 border border-brand-gold/10 rounded-lg p-4"
                    >
                      <div className="flex gap-4">
                        <div className="flex flex-col items-center gap-2 pt-6">
                          <span className="text-lg font-bold text-brand-gold">
                            {index + 1}
                          </span>
                        </div>

                        <div className="flex-1 space-y-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2 md:col-span-2">
                              <Label htmlFor={`task-name-${index}`} className="text-brand-ivory">
                                Nome da Tarefa *
                              </Label>
                              <Input
                                id={`task-name-${index}`}
                                value={task.name}
                                onChange={(e) => updateTask(index, 'name', e.target.value)}
                                placeholder="Ex: Limpar vitrines"
                                className="bg-brand-smoke/50 border-brand-gold/20"
                                required
                              />
                            </div>

                            <div className="space-y-2 md:col-span-2">
                              <Label htmlFor={`task-description-${index}`} className="text-brand-ivory">
                                Descrição
                              </Label>
                              <textarea
                                id={`task-description-${index}`}
                                value={task.description || ''}
                                onChange={(e) => updateTask(index, 'description', e.target.value)}
                                placeholder="Detalhes sobre a tarefa"
                                rows={2}
                                className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-md text-brand-ivory placeholder:text-brand-ivory/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-transparent resize-none"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor={`task-minTime-${index}`} className="text-brand-ivory">
                                Horário Mínimo *
                              </Label>
                              <Input
                                type="time"
                                id={`task-minTime-${index}`}
                                value={task.minTime}
                                onChange={(e) => updateTask(index, 'minTime', e.target.value)}
                                className="bg-brand-smoke/50 border-brand-gold/20"
                                required
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor={`task-maxTime-${index}`} className="text-brand-ivory">
                                Horário Máximo *
                              </Label>
                              <Input
                                type="time"
                                id={`task-maxTime-${index}`}
                                value={task.maxTime}
                                onChange={(e) => updateTask(index, 'maxTime', e.target.value)}
                                className="bg-brand-smoke/50 border-brand-gold/20"
                                required
                              />
                            </div>

                            <div className="space-y-2 md:col-span-2">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`task-photo-${index}`}
                                  checked={task.photoRequired}
                                  onChange={(e) => updateTask(index, 'photoRequired', e.target.checked)}
                                  className="w-4 h-4 rounded border-brand-gold/20 bg-brand-smoke/50 text-brand-gold focus:ring-brand-gold focus:ring-offset-0"
                                />
                                <Label
                                  htmlFor={`task-photo-${index}`}
                                  className="cursor-pointer flex items-center gap-2 text-brand-ivory"
                                >
                                  <Camera className="h-4 w-4" />
                                  Foto obrigatória
                                </Label>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="pt-6">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeTask(index)}
                          >
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/app/processos')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            variant="premium"
          >
            <Save className="w-4 h-4 mr-2" />
            {createMutation.isPending || updateMutation.isPending
              ? 'Salvando...'
              : isEditing
                ? 'Atualizar Processo'
                : 'Criar Processo'}
          </Button>
        </div>
      </form>
    </div>
  );
}
